import React, { useState, useEffect } from 'react';
import QuickCustomerForm from './QuickCustomerForm';
import QuickVehicleForm from './QuickVehicleForm';
import QuickServiceForm from './QuickServiceForm';

const AppointmentForm = ({ 
  appointment, 
  customers, 
  vehicles, 
  services, 
  quotes,
  labels = [],
  onSubmit, 
  onCancel, 
  loading,
  onCreateCustomer,
  onCreateVehicle,
  onCreateService,
  onCreateLabel
}) => {
  const [formData, setFormData] = useState({
    title: '',
    customer_id: '',
    vehicle_id: '',
    start_date: '',
    start_time: '',
    end_date: '',
    end_time: '',
    status: 'scheduled',
    services_planned: '',
    quote_id: '',
    notes: '',
    mechanic_assigned: '',
    visible_client: true,
    label_id: ''
  });

  const [errors, setErrors] = useState({});
  const [availableVehicles, setAvailableVehicles] = useState([]);
  const [availableQuotes, setAvailableQuotes] = useState([]);

  // États pour la création rapide
  const [showQuickCustomerForm, setShowQuickCustomerForm] = useState(false);
  const [creatingCustomer, setCreatingCustomer] = useState(false);
  const [showQuickVehicleForm, setShowQuickVehicleForm] = useState(false);
  const [creatingVehicle, setCreatingVehicle] = useState(false);
  const [showQuickServiceForm, setShowQuickServiceForm] = useState(false);
  const [creatingService, setCreatingService] = useState(false);
  // Création rapide de label
  const [showQuickLabelForm, setShowQuickLabelForm] = useState(false);
  const [creatingLabel, setCreatingLabel] = useState(false);
  const [quickLabelData, setQuickLabelData] = useState({ name: '', color: '#8b5cf6', active: true });

  // Statuts des rendez-vous
  const appointmentStatuses = [
    { value: 'scheduled', label: 'Planifié', color: '#3b82f6' },
    { value: 'in_progress', label: 'En cours', color: '#f59e0b' },
    { value: 'completed', label: 'Terminé', color: '#10b981' },
    { value: 'cancelled', label: 'Annulé', color: '#ef4444' }
  ];

  useEffect(() => {
    if (appointment) {
      // Mode édition
      const startDate = new Date(appointment.start_date);
      const endDate = new Date(appointment.end_date);
      
      setFormData({
        title: appointment.title || '',
        customer_id: appointment.customer_id || '',
        vehicle_id: appointment.vehicle_id || '',
        start_date: startDate.toISOString().split('T')[0],
        start_time: startDate.toTimeString().slice(0, 5),
        end_date: endDate.toISOString().split('T')[0],
        end_time: endDate.toTimeString().slice(0, 5),
        status: appointment.status || 'scheduled',
        services_planned: appointment.services_planned || '',
        quote_id: appointment.quote_id || '',
        notes: appointment.notes || '',
        mechanic_assigned: appointment.mechanic_assigned || '',
        visible_client: appointment.visible_client !== false,
        label_id: appointment.label_id || ''
      });

      if (appointment.customer_id) {
        updateAvailableVehicles(appointment.customer_id);
        updateAvailableQuotes(appointment.customer_id);
      }
    } else {
      // Mode création - définir des valeurs par défaut
      const now = new Date();
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(9, 0, 0, 0);
      
      const endTime = new Date(tomorrow);
      endTime.setHours(10, 0, 0, 0);

      setFormData(prev => ({
        ...prev,
        start_date: tomorrow.toISOString().split('T')[0],
        start_time: '09:00',
        end_date: tomorrow.toISOString().split('T')[0],
        end_time: '10:00'
      }));
    }
  }, [appointment]);

  const updateAvailableVehicles = (customerId) => {
    const customerVehicles = vehicles.filter(v => v.customer_id === customerId);
    setAvailableVehicles(customerVehicles);
  };

  const updateAvailableQuotes = (customerId) => {
    const customerQuotes = quotes.filter(q => 
      q.customer_id === customerId && 
      q.status === 'accepted'
    );
    setAvailableQuotes(customerQuotes);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const nextValue = type === 'checkbox' ? checked : value;
    setFormData(prev => ({
      ...prev,
      [name]: nextValue
    }));

    // Mise à jour des véhicules et devis quand le client change
    if (name === 'customer_id') {
      updateAvailableVehicles(value);
      updateAvailableQuotes(value);
      setFormData(prev => ({
        ...prev,
        vehicle_id: '',
        quote_id: ''
      }));
    }

    // Synchroniser la date de fin avec la date de début
    if (name === 'start_date') {
      setFormData(prev => ({
        ...prev,
        end_date: value
      }));
    }

    // Clear error for the changed field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Création rapide de label
  const handleQuickLabelSubmit = async (e) => {
    e.preventDefault();
    if (!quickLabelData.name.trim()) return;
    setCreatingLabel(true);
    try {
      if (onCreateLabel) {
        const newLabel = await onCreateLabel({
          name: quickLabelData.name.trim(),
          color: quickLabelData.color || '#8b5cf6',
          active: quickLabelData.active !== false
        });
        if (newLabel && newLabel.id) {
          setFormData(prev => ({ ...prev, label_id: newLabel.id }));
        }
      }
      setShowQuickLabelForm(false);
      setQuickLabelData({ name: '', color: '#8b5cf6', active: true });
    } catch (error) {
      console.error('Erreur lors de la création du libellé:', error);
    } finally {
      setCreatingLabel(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Le titre est obligatoire';
    }

    if (!formData.customer_id) {
      newErrors.customer_id = 'Le client est obligatoire';
    }

    if (!formData.start_date) {
      newErrors.start_date = 'La date de début est obligatoire';
    }

    if (!formData.start_time) {
      newErrors.start_time = 'L\'heure de début est obligatoire';
    }

    if (!formData.end_time) {
      newErrors.end_time = 'L\'heure de fin est obligatoire';
    }

    // Vérifier que l'heure de fin est après l'heure de début
    if (formData.start_date && formData.end_date && formData.start_time && formData.end_time) {
      const startDateTime = new Date(`${formData.start_date}T${formData.start_time}`);
      const endDateTime = new Date(`${formData.end_date}T${formData.end_time}`);
      
      if (endDateTime <= startDateTime) {
        newErrors.end_time = 'L\'heure de fin doit être après l\'heure de début';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Fonctions pour la création rapide de client
  const handleQuickCustomerSubmit = async (customerData) => {
    setCreatingCustomer(true);
    try {
      const newCustomer = await onCreateCustomer(customerData);
      
      // Fermer le modal
      setShowQuickCustomerForm(false);
      
      // Sélectionner automatiquement le nouveau client
      if (newCustomer && newCustomer.id) {
        setFormData(prev => ({
          ...prev,
          customer_id: newCustomer.id,
          vehicle_id: '' // Reset vehicle selection
        }));
        
        // Mettre à jour les véhicules disponibles
        updateAvailableVehicles(newCustomer.id);
        updateAvailableQuotes(newCustomer.id);
      }
      
    } catch (error) {
      console.error('Erreur lors de la création du client:', error);
    } finally {
      setCreatingCustomer(false);
    }
  };

  // Fonctions pour la création rapide de véhicule
  const handleQuickVehicleSubmit = async (vehicleData) => {
    setCreatingVehicle(true);
    try {
      const newVehicle = await onCreateVehicle({
        ...vehicleData,
        customer_id: formData.customer_id
      });
      
      // Fermer le modal
      setShowQuickVehicleForm(false);
      
      // Sélectionner automatiquement le nouveau véhicule
      if (newVehicle && newVehicle.id) {
        setFormData(prev => ({
          ...prev,
          vehicle_id: newVehicle.id
        }));
        
        // Mettre à jour les véhicules disponibles
        updateAvailableVehicles(formData.customer_id);
      }
      
    } catch (error) {
      console.error('Erreur lors de la création du véhicule:', error);
    } finally {
      setCreatingVehicle(false);
    }
  };

  // Fonctions pour la création rapide de service
  const handleQuickServiceSubmit = async (serviceData) => {
    setCreatingService(true);
    try {
      const newService = await onCreateService(serviceData);
      
      // Fermer le modal
      setShowQuickServiceForm(false);
      
    } catch (error) {
      console.error('Erreur lors de la création du service:', error);
    } finally {
      setCreatingService(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Construire les dates complètes
    const startDateTime = new Date(`${formData.start_date}T${formData.start_time}`);
    const endDateTime = new Date(`${formData.end_date}T${formData.end_time}`);

    const appointmentData = {
      title: formData.title,
      customer_id: formData.customer_id,
      vehicle_id: formData.vehicle_id || null,
      start_date: startDateTime.toISOString(),
      end_date: endDateTime.toISOString(),
      status: formData.status,
      services_planned: formData.services_planned || null,
      quote_id: formData.quote_id || null,
      notes: formData.notes,
      mechanic_assigned: formData.mechanic_assigned || null,
      reminder_sent: false
    };

    // Inclure la visibilité pour création ET édition
    appointmentData.visible_client = formData.visible_client !== false;
    appointmentData.label_id = formData.label_id || null;

    await onSubmit(appointmentData);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  return (
    <div className="appointment-form-overlay">
      <div className="appointment-form-container">
        <div className="appointment-form-header">
          <h2>{appointment ? 'Modifier le rendez-vous' : 'Nouveau rendez-vous'}</h2>
          <button type="button" onClick={onCancel} className="close-btn">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="appointment-form">
          {/* Informations générales */}
          <div className="form-section">
            <h3>Informations générales</h3>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="title">Titre du rendez-vous *</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  className={errors.title ? 'error' : ''}
                  placeholder="Ex: Révision complète, Diagnostic panne..."
                />
                {errors.title && <span className="error-text">{errors.title}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="status">Statut</label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                >
                  {appointmentStatuses.map(status => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Client et véhicule */}
          <div className="form-section">
            <h3>Client et véhicule</h3>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="customer_id">Client *</label>
                <div className="select-with-add">
                  <select
                    id="customer_id"
                    name="customer_id"
                    value={formData.customer_id}
                    onChange={handleChange}
                    className={errors.customer_id ? 'error' : ''}
                  >
                    <option value="">Sélectionner un client</option>
                    {customers.map(customer => (
                      <option key={customer.id} value={customer.id}>
                        {customer.name} {customer.company && `(${customer.company})`}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="add-btn"
                    onClick={() => setShowQuickCustomerForm(true)}
                    title="Ajouter un nouveau client"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                    </svg>
                  </button>
                </div>
                {errors.customer_id && <span className="error-text">{errors.customer_id}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="vehicle_id">Véhicule</label>
                <div className="select-with-add">
                  <select
                    id="vehicle_id"
                    name="vehicle_id"
                    value={formData.vehicle_id}
                    onChange={handleChange}
                    disabled={!formData.customer_id}
                  >
                    <option value="">Sélectionner un véhicule</option>
                    {availableVehicles.map(vehicle => (
                      <option key={vehicle.id} value={vehicle.id}>
                        {vehicle.brand} {vehicle.model} ({vehicle.license_plate})
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="add-btn"
                    onClick={() => setShowQuickVehicleForm(true)}
                    disabled={!formData.customer_id}
                    title="Ajouter un nouveau véhicule"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                    </svg>
                  </button>
                </div>
                {!formData.customer_id && (
                  <span className="info-text">Sélectionnez d'abord un client pour ajouter un véhicule</span>
                )}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="quote_id">Devis associé (optionnel)</label>
              <select
                id="quote_id"
                name="quote_id"
                value={formData.quote_id}
                onChange={handleChange}
                disabled={!formData.customer_id}
              >
                <option value="">Aucun devis associé</option>
                {availableQuotes.map(quote => (
                  <option key={quote.id} value={quote.id}>
                    {quote.quote_number} - {quote.total?.toFixed(2)}€
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Date et heure */}
          <div className="form-section">
            <h3>Planification</h3>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="start_date">Date *</label>
                <input
                  type="date"
                  id="start_date"
                  name="start_date"
                  value={formData.start_date}
                  onChange={handleChange}
                  className={errors.start_date ? 'error' : ''}
                />
                {errors.start_date && <span className="error-text">{errors.start_date}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="start_time">Heure de début *</label>
                <input
                  type="time"
                  id="start_time"
                  name="start_time"
                  value={formData.start_time}
                  onChange={handleChange}
                  className={errors.start_time ? 'error' : ''}
                />
                {errors.start_time && <span className="error-text">{errors.start_time}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="end_date">Date de fin</label>
                <input
                  type="date"
                  id="end_date"
                  name="end_date"
                  value={formData.end_date}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="end_time">Heure de fin *</label>
                <input
                  type="time"
                  id="end_time"
                  name="end_time"
                  value={formData.end_time}
                  onChange={handleChange}
                  className={errors.end_time ? 'error' : ''}
                />
                {errors.end_time && <span className="error-text">{errors.end_time}</span>}
              </div>
            </div>
          </div>

          {/* Services et notes */}
          <div className="form-section">
            <h3>Détails</h3>
            
            <div className="form-group">
              <label htmlFor="services_planned">Services prévus</label>
              <div className="select-with-add">
                <select
                  id="services_planned"
                  name="services_planned"
                  value={formData.services_planned}
                  onChange={handleChange}
                >
                  <option value="">Sélectionner un service</option>
                  {services.filter(s => s.active).map(service => (
                    <option key={service.id} value={service.id}>
                      {service.name} - {service.base_price}€
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="btn-create-service"
                  onClick={() => setShowQuickServiceForm(true)}
                  title="Créer une nouvelle prestation"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11h-4v4h-2v-4H7v-2h4V7h2v4h4v2z"/>
                  </svg>
                  Nouvelle prestation
                </button>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="label_id">Libellé (optionnel)</label>
              <div className="select-with-add">
                {(() => {
                  const selected = labels.find(l => l.id === formData.label_id);
                  const color = selected?.color || null;
                  return (
                    <span
                      className="label-color-dot"
                      title={selected?.name || 'Aucun libellé'}
                      style={{ backgroundColor: color || '#e5e7eb', border: color ? '1px solid #d1d5db' : '1px dashed #d1d5db' }}
                    />
                  );
                })()}
                <select
                  id="label_id"
                  name="label_id"
                  value={formData.label_id}
                  onChange={handleChange}
                >
                  <option value="">Aucun libellé</option>
                  {labels.map(l => (
                    <option key={l.id} value={l.id}>{l.name}</option>
                  ))}
                </select>
                {onCreateLabel && (
                  <button
                    type="button"
                    className="add-btn"
                    onClick={() => setShowQuickLabelForm(true)}
                    title="Ajouter un nouveau libellé"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                    </svg>
                  </button>
                )}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="notes">Notes</label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows="4"
                placeholder="Notes sur le rendez-vous, problèmes signalés, etc."
              />
            </div>

            <div className="form-group" style={{ display:'flex', alignItems:'center', gap:8 }}>
              <input
                id="visible_client"
                name="visible_client"
                type="checkbox"
                checked={!!formData.visible_client}
                onChange={handleChange}
              />
              <label htmlFor="visible_client" style={{ margin:0 }}>Visible dans l'espace client</label>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" onClick={onCancel} className="btn-cancel">
              Annuler
            </button>
            <button type="submit" disabled={loading} className="btn-submit">
              {loading ? 'Enregistrement...' : (appointment ? 'Mettre à jour' : 'Créer le rendez-vous')}
            </button>
          </div>
        </form>

        <style jsx>{`
          .appointment-form-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
            padding: 20px;
          }

          .appointment-form-container {
            background: white;
            border-radius: 12px;
            width: 100%;
            max-width: 800px;
            max-height: 90vh;
            overflow-y: auto;
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
          }

          .appointment-form-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 24px 24px 0 24px;
            border-bottom: 1px solid #e5e7eb;
            margin-bottom: 24px;
            position: sticky;
            top: 0;
            background: white;
            z-index: 10;
          }

          .appointment-form-header h2 {
            margin: 0;
            font-size: 1.5rem;
            font-weight: 600;
            color: #1f2937;
          }

          .close-btn {
            background: none;
            border: none;
            color: #6b7280;
            cursor: pointer;
            padding: 8px;
            border-radius: 6px;
            transition: all 0.2s ease;
          }

          .close-btn:hover {
            background: #f3f4f6;
            color: #374151;
          }

          .appointment-form {
            padding: 0 24px 24px 24px;
          }

          .form-section {
            margin-bottom: 32px;
            padding-bottom: 24px;
            border-bottom: 1px solid #f3f4f6;
          }

          .form-section:last-of-type {
            border-bottom: none;
          }

          .form-section h3 {
            margin: 0 0 20px 0;
            font-size: 1.2rem;
            font-weight: 600;
            color: #1f2937;
            border-left: 4px solid #3b82f6;
            padding-left: 12px;
          }

          .form-row {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 16px;
            margin-bottom: 16px;
          }

          @media (max-width: 640px) {
            .form-row {
              grid-template-columns: 1fr;
            }
          }

          .form-group {
            position: relative;
            margin-bottom: 16px;
          }

          .form-group label {
            display: block;
            margin-bottom: 6px;
            font-weight: 500;
            color: #374151;
            font-size: 0.9rem;
          }

          .form-group input,
          .form-group select,
          .form-group textarea {
            width: 100%;
            padding: 12px 16px;
            border: 1px solid #d1d5db;
            border-radius: 8px;
            font-size: 0.9rem;
            transition: all 0.2s ease;
            box-sizing: border-box;
          }

          .form-group input:focus,
          .form-group select:focus,
          .form-group textarea:focus {
            outline: none;
            border-color: #3b82f6;
            box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
          }

          .form-group input.error,
          .form-group select.error,
          .form-group textarea.error {
            border-color: #dc2626;
          }

          .form-group textarea {
            resize: vertical;
            min-height: 100px;
          }

          .error-text {
            color: #dc2626;
            font-size: 0.8rem;
            margin-top: 4px;
            display: block;
          }

          .info-text {
            color: #6b7280;
            font-size: 0.8rem;
            margin-top: 4px;
            font-style: italic;
          }

          /* Largeurs des modals harmonisées avec PartForm */
          .quick-customer-modal,
          .quick-vehicle-modal,
          .quick-service-modal {
            max-width: 800px;
            width: 90vw;
          }

          /* Styles pour les grilles des formulaires */
          .quick-customer-form,
          .quick-vehicle-form,
          .quick-service-form {
            padding: 24px;
          }

          .quick-customer-form .form-grid,
          .quick-vehicle-form .form-grid,
          .quick-service-form .form-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
            gap: 20px;
            margin-bottom: 24px;
          }

          .quick-customer-form .full-width,
          .quick-vehicle-form .full-width,
          .quick-service-form .full-width {
            grid-column: 1 / -1;
          }

          /* Amélioration des champs de formulaire pour tous les modals */
          .quick-customer-form .form-group,
          .quick-vehicle-form .form-group,
          .quick-service-form .form-group {
            margin-bottom: 4px;
          }

          .quick-customer-form label,
          .quick-vehicle-form label,
          .quick-service-form label {
            display: block;
            margin-bottom: 6px;
            font-weight: 500;
            color: #374151;
            font-size: 0.875rem;
          }

          .quick-customer-form input,
          .quick-vehicle-form input,
          .quick-service-form input,
          .quick-customer-form select,
          .quick-vehicle-form select,
          .quick-service-form select {
            width: 100%;
            padding: 10px 12px;
            border: 1px solid #d1d5db;
            border-radius: 6px;
            font-size: 0.875rem;
            transition: border-color 0.2s ease, box-shadow 0.2s ease;
          }

          .quick-customer-form input:focus,
          .quick-vehicle-form input:focus,
          .quick-service-form input:focus,
          .quick-customer-form select:focus,
          .quick-vehicle-form select:focus,
          .quick-service-form select:focus {
            outline: none;
            border-color: #3b82f6;
            box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
          }

          .quick-customer-form .error-message,
          .quick-vehicle-form .error-message,
          .quick-service-form .error-message {
            color: #ef4444;
            font-size: 0.75rem;
            margin-top: 4px;
            display: block;
          }

          .select-with-add {
            display: flex;
            gap: 8px;
            align-items: flex-start;
          }

          .label-color-dot {
            width: 18px;
            height: 18px;
            border-radius: 9999px;
            display: inline-block;
            margin-top: 12px;
            flex-shrink: 0;
          }

          .select-with-add select {
            flex: 1;
          }

          .add-btn {
            background: #10b981;
            color: white;
            border: none;
            border-radius: 6px;
            padding: 10px;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.2s ease;
            flex-shrink: 0;
            height: 42px;
            width: 42px;
          }

          .add-btn:hover {
            background: #059669;
            transform: scale(1.05);
          }

          .add-btn:active {
            transform: scale(0.95);
          }

          .add-btn:disabled {
            background: #9ca3af;
            cursor: not-allowed;
            transform: none;
          }

          .add-btn:disabled:hover {
            background: #9ca3af;
            transform: none;
          }

          .btn-create-service {
            background: #8b5cf6;
            color: white;
            border: none;
            border-radius: 6px;
            padding: 8px 16px;
            cursor: pointer;
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 0.875rem;
            font-weight: 500;
            transition: all 0.2s ease;
          }

          .btn-create-service:hover {
            background: #7c3aed;
            transform: translateY(-1px);
          }

          .btn-create-service:active {
            transform: translateY(0);
          }

          .form-actions {
            display: flex;
            gap: 12px;
            justify-content: flex-end;
            margin-top: 32px;
            padding-top: 24px;
            border-top: 1px solid #e5e7eb;
            position: sticky;
            bottom: 0;
            background: white;
          }

          .btn-cancel,
          .btn-submit {
            padding: 12px 24px;
            border-radius: 8px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s ease;
            border: none;
            font-size: 0.9rem;
          }

          .btn-cancel {
            background: #f3f4f6;
            color: #374151;
          }

          .btn-cancel:hover {
            background: #e5e7eb;
          }

          .btn-submit {
            background: #3b82f6;
            color: white;
          }

          .btn-submit:hover:not(:disabled) {
            background: #2563eb;
          }

          .btn-submit:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }
        `}</style>
      </div>

      {/* Modal de création client rapide */}
      {showQuickCustomerForm && (
        <QuickCustomerForm
          onSubmit={handleQuickCustomerSubmit}
          onCancel={() => setShowQuickCustomerForm(false)}
          loading={creatingCustomer}
        />
      )}

      {/* Modal de création véhicule rapide */}
      {showQuickVehicleForm && (
        <QuickVehicleForm
          customerId={formData.customer_id}
          onSubmit={handleQuickVehicleSubmit}
          onCancel={() => setShowQuickVehicleForm(false)}
          loading={creatingVehicle}
        />
      )}

      {/* Modal de création service rapide */}
      {showQuickServiceForm && (
        <QuickServiceForm
          onSubmit={handleQuickServiceSubmit}
          onCancel={() => setShowQuickServiceForm(false)}
          loading={creatingService}
        />
      )}

      {/* Modal création rapide de libellé */}
      {showQuickLabelForm && (
        <div className="appointment-form-overlay">
          <div className="appointment-form-container quick-service-modal">
            <div className="appointment-form-header">
              <h2>Nouveau libellé</h2>
              <button type="button" onClick={() => setShowQuickLabelForm(false)} className="close-btn">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
              </button>
            </div>
            <form onSubmit={handleQuickLabelSubmit} className="quick-service-form">
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="ql_name">Nom du libellé</label>
                  <input
                    id="ql_name"
                    type="text"
                    value={quickLabelData.name}
                    onChange={(e) => setQuickLabelData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Ex: Perso, Pro, Urgent"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="ql_color">Couleur</label>
                  <input
                    id="ql_color"
                    type="color"
                    value={quickLabelData.color}
                    onChange={(e) => setQuickLabelData(prev => ({ ...prev, color: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="ql_active">Actif</label>
                  <input
                    id="ql_active"
                    type="checkbox"
                    checked={quickLabelData.active}
                    onChange={(e) => setQuickLabelData(prev => ({ ...prev, active: e.target.checked }))}
                  />
                </div>
              </div>
              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowQuickLabelForm(false)}>Annuler</button>
                <button type="submit" className="btn-submit" disabled={creatingLabel}>{creatingLabel ? 'Création...' : 'Créer'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppointmentForm;
